import { tw } from "@careinfo/tailwind";
import loadable from "@loadable/component";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-toastify";
import APIS from "../../constant/Endpoint";
import useGetHook from "../../customHooks/useGetHook";
import { postApiFormData } from "../../helpers/AxiosInstance";
import { useNavigate } from "react-router-dom";
import { usePDFPrintHook } from "@careinfo/layout";
import Spinner from "../../components/Reusable/Spinner";

const AddSalesComponent = loadable(
  () => import("../../components/Sales/AddSales")
);

const AddSales = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { downloadPrint, isPrintLoading } = usePDFPrintHook();
  const [openPrintPopup, setOpenPrintPopup] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const { refetch: numbSeriresRefetch } = useGetHook({
    queryKey: ["number-serires-sales"],
    url: APIS.NUMBER_SERIRES,
    params: {
      type: "Sales Invoice",
    },
  });

  const { data: companyData, isLoading: isCompanyDataLoading } = useGetHook({
    url: APIS.COMPANY_PROFILE,
    queryKey: ["company-profile"],
    params: {},
  });

  const { refetch: entryNoRefetch } = useGetHook({
    queryKey: ["sales"],
    url: APIS.SALES_PURCHASE,
    params: {
      limit: 1,
      offset: 0,
      type: "Sales Invoice",
    },
  });

  const handleCreate = async (values: any, isDraft: any, setError: any) => {
    setIsLoading(true);
    const url = APIS.SALES_PURCHASE;
    const formData = new FormData();
    formData.append("voucher_date", values?.voucher_date ?? null);
    formData.append("reference_number", values?.reference_number ?? "");
    values?.reference_date
      ? formData.append("reference_date", values?.reference_date)
      : null;
    formData.append("number_series", values?.number_series);
    formData.append("entry_number", values?.entry_number);
    formData.append("party", values?.party);
    formData.append("tax_rate", values?.tax_rate ?? "0");
    formData.append("taxable_amount", values?.taxable_amount);
    formData.append("total_amount", values?.total_amount);
    formData.append("discount", values?.total_discount);
    formData.append("tax_amount", values?.vat_tax_amount);
    formData.append("type", "Sales Invoice");
    isDraft
      ? formData.append("status", "Draft")
      : formData.append("status", "Success");
    if (values?.items) {
      const cleanedItems = values.items.map(
        ({
          available_quantity,
          is_maintain_stock,
          is_taxable,
          last_purchase_price,
          serial_number,
          unit,
          ...rest
        }) => ({
          ...rest,
          is_taxable: is_taxable ? "true" : "false",
        })
      );
      formData.append("items", JSON.stringify(cleanedItems));
    }
    if (values?.attachment?.[0] && typeof values?.attachment !== "string") {
      formData.append("attachment", values?.attachment?.[0]);
    }
    values?.bill_number
      ? formData.append("bill_number", values?.bill_number)
      : null;
    values?.narration ? formData.append("narration", values?.narration) : null;
    try {
      const res = await postApiFormData({ url, formData, setError });
      if (res?.status === 201) {
        queryClient.invalidateQueries({
          queryKey: ["sales"],
          refetchType: "active",
        });
        if (!isDraft) {
          setSelectedOrder(res?.data?.id);
          setOpenPrintPopup(true);
        } else {
          navigate("/accounting/sales");
        }
        toast.success("Create Successfully");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = async () => {
    const url = APIS.BILL_PRINT;
    let formData = {
      ids: [selectedOrder],
      type: "Sales Invoice",
    };

    try {
      const res = await downloadPrint(url, formData);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <div
        className={tw`w-full flex justify-left gap-x-2 mb-4 uppercase font-extrabold items-center`}
      >
        <span className={tw`text-[#5E5757] font-bold text-xl`}>Add Sales</span>
      </div>
      {isCompanyDataLoading ? (
        <Spinner />
      ) : (
        <AddSalesComponent
          handleCreate={handleCreate}
          isLoading={isLoading}
          openPrintPopup={openPrintPopup}
          setOpenPrintPopup={setOpenPrintPopup}
          handlePrint={handlePrint}
          isPrintLoading={isPrintLoading}
          companyData={companyData}
          numbSeriesRefetch={numbSeriresRefetch}
          entryNoRefetch={entryNoRefetch}
        />
      )}
    </>
  );
};

export default AddSales;
